/**
 * RoleManagement.js — global-admin console for per-project role grants
 * and per-user account-type (org_type) overrides.
 *
 * Two responsibilities:
 *   1) Project roles — search users, see what (project, role) pairs they
 *      already hold, add/remove a row in user_project_roles. This is the
 *      original surface of the component.
 *   2) Account type (org_type) — admin-only override. Users self-pick
 *      "individual" vs "organization" once during onboarding; after
 *      onboarded_at is stamped, the API rejects self-edits with 403.
 *      Admin overrides go through adminUpdateUserAttrs.php and do NOT
 *      bump manually_edited_at (it stays the user's last self-edit time).
 *
 * Auth model: every request goes through apiCall(), which attaches the
 * caller's Firebase ID token. Server endpoints decide whether they need
 * it (e.g. updateUserRoles.php / adminUpdateUserAttrs.php require a
 * verified admin; the four read endpoints currently don't require auth
 * but are gated client-side by routing this component behind isGlobalAdmin
 * in AdminDashboard).
 *
 * Known long-tail (pre-existing, tracked separately): fetchUserU.php and
 * fetchUsers.php currently SELECT * over the users table without server-
 * side authz. That's a pre-existing exposure unrelated to this PR; logged
 * under "TODOS.md → Phase 2 auth hardening".
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Button,
    Chip,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Select,
    Paper,
    TextField,
    Grid,
} from '@mui/material';
import { apiCall, ApiError } from './api';
import { ORG_TYPE, ORG_TYPE_OPTIONS } from './utils/constants';

function labelForOrg(orgType) {
    return (
        ORG_TYPE_OPTIONS.find((o) => o.value === orgType)?.label || 'Individual'
    );
}

const UserRoleManager = ({ isGlobalAdmin, userProjectIds }) => {
    const [selectedUser, setSelectedUser] = useState(null);
    const [selectedProject, setSelectedProject] = useState(null);
    const [selectedRole, setSelectedRole] = useState(null);
    const [roles, setRoles] = useState([]);
    const [users, setUsers] = useState([]);
    const [userData, setUserData] = useState([]);
    const [projects, setProjects] = useState([]);
    const [searchUser, setSearchUser] = useState('');

    // org_type admin-edit UI state. Scoped to RoleManagement because this
    // is the only consumer of adminUpdateUserAttrs.php right now.
    const [editingOrg, setEditingOrg] = useState(false);
    const [orgDraft, setOrgDraft] = useState(ORG_TYPE.INDIVIDUAL);
    const [orgSaving, setOrgSaving] = useState(false);
    const [orgError, setOrgError] = useState(null);

    const fetchUsers = useCallback(async () => {
        try {
            const data = await apiCall('fetchUserU.php');
            setUsers(data?.users || []);
        } catch (err) {
            console.error('Error fetching users:', err);
        }
    }, []);

    const fetchRoles = useCallback(async () => {
        try {
            const data = await apiCall('selectRoles.php');
            setRoles(data?.roles || []);
        } catch (err) {
            console.error('Error fetching roles:', err);
        }
    }, []);

    const fetchUserData = useCallback(async () => {
        try {
            const data = await apiCall('fetchUsers.php');
            setUserData(data?.users || []);
        } catch (err) {
            console.error('Error fetching user-role rows:', err);
        }
    }, []);

    const fetchProjects = useCallback(async () => {
        try {
            const data = await apiCall('fetchProjects.php');
            setProjects(data?.projects || []);
        } catch (err) {
            console.error('Error fetching projects:', err);
        }
    }, []);

    useEffect(() => {
        fetchUsers();
        fetchProjects();
        fetchUserData();
        fetchRoles();
    }, [fetchUsers, fetchProjects, fetchUserData, fetchRoles]);

    const handleSubmit = async () => {
        if (!selectedUser || !selectedProject || !selectedRole) {
            alert('Please select a user, project, and role.');
            return;
        }

        try {
            await apiCall('updateUserRoles.php', {
                method: 'POST',
                body: {
                    action: 'add',
                    user_id: selectedUser.user_id,
                    projectsid: selectedProject,
                    role: selectedRole,
                },
            });
            alert('Role added successfully!');
            // Refresh role-rows so the new grant shows in the right pane.
            fetchUserData();
        } catch (err) {
            console.error('Error adding role:', err);
            alert('Error adding role.');
        }
    };

    const handleDelete = async (row) => {
        if (!selectedUser) {
            alert('Please select a user.');
            return;
        }

        try {
            await apiCall('updateUserRoles.php', {
                method: 'POST',
                body: {
                    action: 'remove',
                    user_id: selectedUser.user_id,
                    projectsid: row.projectsid,
                    role: row.role_id,
                },
            });
            alert('Role removed successfully!');
            fetchUserData();
        } catch (err) {
            console.error('Error removing role:', err);
            alert('Error removing role.');
        }
    };

    const handleSelectUser = (user) => {
        setSelectedUser(user);
        // Close any open org-type editor when the row changes so we don't
        // accidentally write the previous user's draft against the new uid.
        setEditingOrg(false);
        setOrgError(null);
        setOrgDraft(user?.org_type || ORG_TYPE.INDIVIDUAL);
    };

    const handleStartOrgEdit = () => {
        setOrgDraft(selectedUser?.org_type || ORG_TYPE.INDIVIDUAL);
        setOrgError(null);
        setEditingOrg(true);
    };

    const handleCancelOrgEdit = () => {
        setEditingOrg(false);
        setOrgError(null);
    };

    const handleSaveOrgEdit = async () => {
        if (!selectedUser) return;
        if (!Object.values(ORG_TYPE).includes(orgDraft)) {
            setOrgError('Pick one.');
            return;
        }
        // No-op short-circuit: don't burn a write (or an audit-log line) on
        // an admin who opened the editor without changing anything.
        if (orgDraft === (selectedUser.org_type || ORG_TYPE.INDIVIDUAL)) {
            setEditingOrg(false);
            return;
        }

        setOrgSaving(true);
        setOrgError(null);
        try {
            await apiCall('adminUpdateUserAttrs.php', {
                method: 'POST',
                body: {
                    target_user_id: selectedUser.user_id,
                    attrs: { org_type: orgDraft },
                },
            });
            // Optimistic-ish: re-fetch the users list so the chip reflects
            // canonical server state, and re-bind selectedUser to the new
            // row (object identity changes after the fetch).
            await fetchUsers();
            setSelectedUser((prev) =>
                prev ? { ...prev, org_type: orgDraft } : prev
            );
            setEditingOrg(false);
        } catch (err) {
            const msg =
                err instanceof ApiError
                    ? err.message
                    : 'Could not update account type. Please try again.';
            setOrgError(msg);
            console.error('adminUpdateUserAttrs failed:', err);
        } finally {
            setOrgSaving(false);
        }
    };

    return (
        <Box>
            <Grid container spacing={2} padding={2} direction="row">
                <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexDirection: 'column' }}>
                    <Typography variant="h4" gutterBottom>User Role Management</Typography>
                </Grid>
                <Grid item xs={4} lg={4} sx={{ height: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexDirection: 'column', }}>
                    <Typography variant="h6" gutterBottom>Select a User</Typography>
                    <TextField
                        value={searchUser}
                        onChange={(e) => setSearchUser(e.target.value)}
                        label="Search User"
                        fullWidth
                    />
                    <TableContainer component={Paper} >
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Email</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {users && users.length > 0 && users
                                    .filter(user => (user.display_name || '').toLowerCase().includes(searchUser.toLowerCase())).map((user) => (
                                        <TableRow key={user.user_id} onClick={() => handleSelectUser(user)} hover>
                                            <TableCell>{user.display_name}</TableCell>
                                            <TableCell>{user.email}</TableCell>
                                        </TableRow>
                                    ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Grid>
                <Grid item xs={8} lg={8} padding={2} sx={{ height: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexDirection: 'column', }}>
                    {selectedUser && (
                        <Paper
                            elevation={1}
                            sx={{
                                width: '100%',
                                padding: 2,
                                marginBottom: 2,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1,
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                                <Typography variant="h6" sx={{ margin: 0 }}>
                                    {selectedUser.display_name || '(no name)'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {selectedUser.email}
                                </Typography>
                                {!editingOrg && (
                                    <Chip
                                        label={`Account type: ${labelForOrg(selectedUser.org_type)}`}
                                        color={
                                            selectedUser.org_type === ORG_TYPE.ORGANIZATION
                                                ? 'primary'
                                                : 'default'
                                        }
                                        size="small"
                                    />
                                )}
                                {isGlobalAdmin && !editingOrg && (
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={handleStartOrgEdit}
                                    >
                                        Edit account type
                                    </Button>
                                )}
                            </Box>
                            {editingOrg && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                    <Typography variant="body2">Account type:</Typography>
                                    <Select
                                        size="small"
                                        value={orgDraft}
                                        onChange={(e) => setOrgDraft(e.target.value)}
                                        disabled={orgSaving}
                                    >
                                        {ORG_TYPE_OPTIONS.map((opt) => (
                                            <MenuItem key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    <Button
                                        size="small"
                                        variant="contained"
                                        color="primary"
                                        onClick={handleSaveOrgEdit}
                                        disabled={orgSaving}
                                    >
                                        {orgSaving ? 'Saving…' : 'Save'}
                                    </Button>
                                    <Button
                                        size="small"
                                        variant="text"
                                        onClick={handleCancelOrgEdit}
                                        disabled={orgSaving}
                                    >
                                        Cancel
                                    </Button>
                                </Box>
                            )}
                            {orgError && (
                                <Typography variant="body2" color="error">
                                    {orgError}
                                </Typography>
                            )}
                        </Paper>
                    )}
                    <TableContainer component={Paper} fullwidth>
                        <Table fullwidth>
                            <TableHead fullwidth>
                                <TableRow>
                                    <TableCell>User Name</TableCell>
                                    <TableCell>Email</TableCell>
                                    <TableCell>Project</TableCell>
                                    <TableCell>Role</TableCell>
                                    <TableCell>Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {userData && userData.length > 0 && selectedUser && userData
                                    .filter(userData => userData.user_id === selectedUser.user_id)
                                    .map((userData) => (
                                        <TableRow key={`${userData.user_id}-${userData.projectsid}-${userData.role_id}`}>
                                            <TableCell>{userData.display_name}</TableCell>
                                            <TableCell>{userData.email}</TableCell>
                                            <TableCell>{userData.project_name}</TableCell>
                                            <TableCell>{userData.role_name}</TableCell>
                                            <TableCell>
                                                {isGlobalAdmin && userData.role_name &&
                                                    <Button variant="contained" color="secondary" onClick={() => handleDelete(userData)} >
                                                        Remove Role
                                                    </Button>
                                                }
                                            </TableCell>
                                        </TableRow>
                                    ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    {isGlobalAdmin && (
                    <Paper elevation={2} sx={{ height: 200, width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexDirection: 'column' }}>
                        <Typography variant="h6" padding={1}>
                            Add A New User Role
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexDirection: 'row' }}>
                            <Select
                                value={selectedProject || ""}
                                onChange={(e) => setSelectedProject(e.target.value)}
                                displayEmpty
                                sx={{ margin: 2 }}
                            >
                                <MenuItem value="" disabled>Select Project</MenuItem>
                                {projects && projects.length > 0 &&
                                    projects.map((project) => (
                                        <MenuItem key={project.projectsid} value={project.projectsid}>
                                            {project.project_name}
                                        </MenuItem>
                                    ))
                                }
                            </Select>
                            <Select
                                value={selectedRole || ""}
                                onChange={(e) => setSelectedRole(e.target.value)}
                                displayEmpty
                                sx={{ margin: 2 }}
                            >
                                <MenuItem value="" disabled>Select Role</MenuItem>
                                {roles && roles.length > 0 &&
                                    roles.map((roles) => (
                                        <MenuItem key={roles.role_id} value={roles.role_id}>
                                            {roles.role_name}
                                        </MenuItem>
                                    ))
                                }
                            </Select>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexDirection: 'row', marginBottom: 2.5 }}>
                            <Button variant="contained" color="secondary" onClick={() => handleSubmit()}>ADD ROLE</Button>
                        </Box>

                    </Paper>
                    )}
                </Grid>
            </Grid>
        </Box>
    );
};

export default UserRoleManager;
